export const introToComputerScienceCourse = {
  id: 'intro-computer-science',
  title: 'Introduction to Computer Science',
  description: 'Fundamental concepts of computer science including algorithms, data structures, computational thinking, and problem-solving techniques.',
  category: 'Coding',
  credits: 3,
  duration: '15 weeks',
  color: 'purple',
  modules: [
    {
      id: 'module-1',
      title: 'Computational Thinking and Problem Solving',
      description: 'Learn the fundamental principles of computational thinking, algorithm design, and systematic problem-solving approaches.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Introduction to Computational Thinking',
          type: 'coding-interactive',
          content: 'Understanding how computers solve problems through decomposition, pattern recognition, abstraction, and algorithms.',
          exercise: {
            instructions: 'Write a function called "analyzePattern" that takes an array of numbers and returns an object with the pattern analysis. The function should identify if the sequence is arithmetic (constant difference), geometric (constant ratio), or neither. Test with [1, 3, 5, 7, 9] which should return { type: "arithmetic", value: 2 }.',
            initialCode: `function analyzePattern(numbers) {
  if (numbers.length < 2) {
    return { type: 'neither', value: null };
  }
  
  // Check for arithmetic sequence
  const firstDiff = numbers[1] - numbers[0];
  let isArithmetic = true;
  
  for (let i = 2; i < numbers.length; i++) {
    if (numbers[i] - numbers[i-1] !== firstDiff) {
      isArithmetic = false;
      break;
    }
  }
  
  if (isArithmetic) {
    return { type: 'arithmetic', value: firstDiff };
  }
  
  // Check for geometric sequence
  if (numbers[0] === 0) {
    return { type: 'neither', value: null };
  }
  
  const firstRatio = numbers[1] / numbers[0];
  let isGeometric = true;
  
  for (let i = 2; i < numbers.length; i++) {
    if (numbers[i-1] === 0 || numbers[i] / numbers[i-1] !== firstRatio) {
      isGeometric = false;
      break;
    }
  }
  
  if (isGeometric) {
    return { type: 'geometric', value: firstRatio };
  }
  
  return { type: 'neither', value: null };
}

// Test the function
console.log(analyzePattern([1, 3, 5, 7, 9]));`,
            expectedOutput: `{ type: 'arithmetic', value: 2 }`,
            hints: [
              'Check if consecutive differences are the same for arithmetic sequences',
              'Check if consecutive ratios are the same for geometric sequences',
              'Return the common difference or ratio as the value'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Algorithm Design Principles',
          type: 'coding-interactive',
          content: 'Learning systematic approaches to designing efficient algorithms and breaking down complex problems.',
          exercise: {
            instructions: 'Create a function "findLargestTwo" that takes an array of numbers and returns the two largest distinct numbers in descending order. Handle edge cases appropriately. Test with [3, 7, 1, 8, 2, 7] which should return [8, 7].',
            initialCode: `function findLargestTwo(numbers) {
  if (numbers.length < 2) {
    return numbers.slice();
  }
  
  // Remove duplicates and sort in descending order
  const uniqueNumbers = [...new Set(numbers)].sort((a, b) => b - a);
  
  // Return the top two numbers
  return uniqueNumbers.slice(0, 2);
}

// Test the function
console.log(findLargestTwo([3, 7, 1, 8, 2, 7]));`,
            expectedOutput: `[8, 7]`,
            hints: [
              'Sort the array or use two variables to track largest values',
              'Make sure the two numbers are distinct',
              'Handle arrays with less than 2 distinct numbers'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Pseudocode and Flow Control',
          type: 'coding-interactive',
          content: 'Understanding how to express algorithms in pseudocode and implement control structures effectively.',
          exercise: {
            instructions: 'Implement a function "gradeCalculator" that takes an array of scores (0-100) and returns the letter grade based on average. A: 90+, B: 80-89, C: 70-79, D: 60-69, F: below 60. Test with [85, 92, 78, 88] which should return "B".',
            initialCode: `function gradeCalculator(scores) {
  if (scores.length === 0) {
    return 'F';
  }
  
  const sum = scores.reduce((acc, score) => acc + score, 0);
  const average = sum / scores.length;
  
  if (average >= 90) return 'A';
  if (average >= 80) return 'B';
  if (average >= 70) return 'C';
  if (average >= 60) return 'D';
  return 'F';
}

// Test the function
console.log(gradeCalculator([85, 92, 78, 88]));`,
            expectedOutput: `B`,
            hints: [
              'Calculate the average of all scores first',
              'Use conditional statements to determine the grade',
              'Handle empty arrays appropriately'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 1 Assessment: Problem Solving',
        instructions: 'Create a function "optimizePath" that finds the shortest path length in a grid where you can only move right or down. The grid contains obstacles (1) and free spaces (0). Test with a 3x3 grid.',
        initialCode: `function optimizePath(grid) {
  if (!grid || grid.length === 0 || grid[0].length === 0) {
    return -1;
  }
  
  const rows = grid.length;
  const cols = grid[0].length;
  
  // If start or end is blocked, no path exists
  if (grid[0][0] === 1 || grid[rows-1][cols-1] === 1) {
    return -1;
  }
  
  // Create DP table
  const dp = Array(rows).fill().map(() => Array(cols).fill(Infinity));
  
  // Base case
  dp[0][0] = 1;
  
  // Fill the DP table
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      if (grid[i][j] === 1) continue; // Skip obstacles
      
      if (i > 0 && dp[i-1][j] !== Infinity) {
        dp[i][j] = Math.min(dp[i][j], dp[i-1][j] + 1);
      }
      if (j > 0 && dp[i][j-1] !== Infinity) {
        dp[i][j] = Math.min(dp[i][j], dp[i][j-1] + 1);
      }
    }
  }
  
  return dp[rows-1][cols-1] === Infinity ? -1 : dp[rows-1][cols-1];
}

// Test the function
const testGrid = [[0,0,0],[0,1,0],[0,0,0]];
console.log(optimizePath(testGrid));`,
        expectedOutput: `5`,
        hints: [
          'Use dynamic programming approach',
          'Consider obstacles as blocked paths',
          'Start from top-left, end at bottom-right'
        ]
      }
    },
    {
      id: 'module-2',
      title: 'Data Structures and Algorithms',
      description: 'Explore fundamental data structures (arrays, lists, stacks, queues) and basic algorithms for searching and sorting.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Arrays and Linear Search',
          type: 'coding-interactive',
          content: 'Understanding array operations and implementing linear search algorithms with efficiency analysis.',
          exercise: {
            instructions: 'Implement a function "smartSearch" that searches for a target value in an array. If found, return the index. If not found, return the index where it should be inserted to keep the array sorted. Test with [1, 3, 5, 7] and target 4 which should return 2.',
            initialCode: `function smartSearch(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    
    if (arr[mid] === target) {
      return mid;
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  
  // If not found, left is the insertion point
  return left;
}

// Test the function
console.log(smartSearch([1, 3, 5, 7], 4));`,
            expectedOutput: `2`,
            hints: [
              'Check if the target exists first',
              'If not found, find the correct insertion position',
              'Consider using binary search for efficiency'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Stacks and Queues',
          type: 'coding-interactive',
          content: 'Implementing stack and queue data structures and understanding their applications in computer science.',
          exercise: {
            instructions: 'Create a function "balancedParentheses" that checks if parentheses, brackets, and braces are properly balanced in a string using a stack approach. Test with "({[]})" which should return true.',
            initialCode: `function balancedParentheses(str) {
  const stack = [];
  const pairs = {
    ')': '(',
    ']': '[',
    '}': '{'
  };
  
  for (let char of str) {
    if (char === '(' || char === '[' || char === '{') {
      stack.push(char);
    } else if (char === ')' || char === ']' || char === '}') {
      if (stack.length === 0 || stack.pop() !== pairs[char]) {
        return false;
      }
    }
  }
  
  return stack.length === 0;
}

// Test the function
console.log(balancedParentheses("({[]})"));`,
            expectedOutput: `true`,
            hints: [
              'Use a stack to track opening brackets',
              'Push opening brackets, check matching for closing brackets',
              'The stack should be empty at the end if balanced'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Sorting Algorithms',
          type: 'coding-interactive',
          content: 'Understanding and implementing basic sorting algorithms like bubble sort, selection sort, and insertion sort.',
          exercise: {
            instructions: 'Implement an "insertionSort" function that sorts an array of numbers in ascending order. The function should modify the array in-place and return it. Test with [5, 2, 4, 1, 3] which should return [1, 2, 3, 4, 5].',
            initialCode: `function insertionSort(arr) {
  for (let i = 1; i < arr.length; i++) {
    let current = arr[i];
    let j = i - 1;
    
    // Move elements that are greater than current to one position ahead
    while (j >= 0 && arr[j] > current) {
      arr[j + 1] = arr[j];
      j--;
    }
    
    arr[j + 1] = current;
  }
  
  return arr;
}

// Test the function
console.log(insertionSort([5, 2, 4, 1, 3]));`,
            expectedOutput: `[1, 2, 3, 4, 5]`,
            hints: [
              'Start from the second element (index 1)',
              'Compare each element with previous elements',
              'Insert the element at the correct position'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 2 Assessment: Data Structures',
        instructions: 'Create a "CustomStack" class with methods push, pop, peek, isEmpty, and getMin that returns the minimum element in O(1) time. Test by pushing [5, 2, 8, 1, 3] then calling getMin() which should return 1.',
        initialCode: `class CustomStack {
  constructor() {
    this.stack = [];
    this.minStack = [];
  }
  
  push(value) {
    this.stack.push(value);
    
    if (this.minStack.length === 0 || value <= this.getMin()) {
      this.minStack.push(value);
    }
  }
  
  pop() {
    if (this.isEmpty()) {
      return null;
    }
    
    const popped = this.stack.pop();
    
    if (popped === this.getMin()) {
      this.minStack.pop();
    }
    
    return popped;
  }
  
  peek() {
    if (this.isEmpty()) {
      return null;
    }
    return this.stack[this.stack.length - 1];
  }
  
  isEmpty() {
    return this.stack.length === 0;
  }
  
  getMin() {
    if (this.minStack.length === 0) {
      return null;
    }
    return this.minStack[this.minStack.length - 1];
  }
}

// Test the class
const stack = new CustomStack();
stack.push(5);
stack.push(2);
stack.push(8);
stack.push(1);
stack.push(3);
console.log(stack.getMin());`,
        expectedOutput: `1`,
        hints: [
          'Use an additional stack to track minimum values',
          'Update the min stack when pushing/popping',
          'Handle edge cases for empty stack'
        ]
      }
    },
    {
      id: 'module-3',
      title: 'Recursion and Mathematical Foundations',
      description: 'Master recursive thinking, mathematical foundations of computer science, and recursive algorithms.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Understanding Recursion',
          type: 'coding-interactive',
          content: 'Learning recursive thinking, base cases, and recursive cases with practical examples.',
          exercise: {
            instructions: 'Write a recursive function "fibonacci" that calculates the nth Fibonacci number. Include proper base cases and recursive logic. Test with n=8 which should return 21.',
            initialCode: `function fibonacci(n) {
  // Base cases
  if (n <= 0) return 0;
  if (n === 1) return 1;
  
  // Recursive case
  return fibonacci(n - 1) + fibonacci(n - 2);
}

// Optimized version with memoization for better performance
function fibonacciMemo(n, memo = {}) {
  if (n in memo) return memo[n];
  if (n <= 0) return 0;
  if (n === 1) return 1;
  
  memo[n] = fibonacciMemo(n - 1, memo) + fibonacciMemo(n - 2, memo);
  return memo[n];
}

// Test the function
console.log(fibonacci(8));`,
            expectedOutput: `21`,
            hints: [
              'Base cases: fib(0) = 0, fib(1) = 1',
              'Recursive case: fib(n) = fib(n-1) + fib(n-2)',
              'Make sure to handle negative inputs'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Tree Traversal',
          type: 'coding-interactive',
          content: 'Understanding tree structures and implementing recursive tree traversal algorithms.',
          exercise: {
            instructions: 'Implement a function "treeSum" that calculates the sum of all values in a binary tree using recursion. The tree is represented as objects with value, left, and right properties. Test with a tree that sums to 15.',
            initialCode: `function treeSum(root) {
  // Base case: if node is null, return 0
  if (!root) {
    return 0;
  }
  
  // Recursive case: current node value + sum of left + sum of right
  return root.value + treeSum(root.left) + treeSum(root.right);
}

// Test the function
const testTree = {
  value: 5,
  left: {
    value: 3,
    left: { value: 1, left: null, right: null },
    right: { value: 4, left: null, right: null }
  },
  right: {
    value: 2,
    left: null,
    right: null
  }
};
console.log(treeSum(testTree));`,
            expectedOutput: `15`,
            hints: [
              'Base case: if node is null, return 0',
              'Recursive case: node.value + treeSum(left) + treeSum(right)',
              'Handle null nodes properly'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Mathematical Algorithms',
          type: 'coding-interactive',
          content: 'Implementing mathematical algorithms using recursion and understanding computational complexity.',
          exercise: {
            instructions: 'Create a recursive function "gcd" that finds the Greatest Common Divisor of two positive integers using Euclidean algorithm. Test with gcd(12, 8) which should return 4.',
            initialCode: `function gcd(a, b) {
  // Base case: if b is 0, GCD is a
  if (b === 0) {
    return a;
  }
  
  // Recursive case: GCD(a, b) = GCD(b, a % b)
  return gcd(b, a % b);
}

// Alternative iterative version for comparison
function gcdIterative(a, b) {
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
}

// Test the function
console.log(gcd(12, 8));`,
            expectedOutput: `4`,
            hints: [
              'Base case: if b is 0, return a',
              'Recursive case: gcd(b, a % b)',
              'The Euclidean algorithm is efficient for finding GCD'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 3 Assessment: Recursion',
        instructions: 'Implement a recursive function "permutations" that generates all permutations of a given string and returns them as an array. Test with "abc" which should return ["abc", "acb", "bac", "bca", "cab", "cba"].',
        initialCode: `function permutations(str) {
  // Base case: if string length is 1, return array with that string
  if (str.length <= 1) {
    return [str];
  }
  
  const result = [];
  
  // For each character in the string
  for (let i = 0; i < str.length; i++) {
    const currentChar = str[i];
    const remainingChars = str.slice(0, i) + str.slice(i + 1);
    
    // Get all permutations of remaining characters
    const remainingPerms = permutations(remainingChars);
    
    // Add current character to the beginning of each permutation
    for (let perm of remainingPerms) {
      result.push(currentChar + perm);
    }
  }
  
  return result;
}

// Test the function
console.log(permutations("abc"));`,
        expectedOutput: `["abc", "acb", "bac", "bca", "cab", "cba"]`,
        hints: [
          'Base case: if string length is 1, return array with that string',
          'For each character, find permutations of remaining characters',
          'Combine current character with each permutation'
        ]
      }
    },
    {
      id: 'module-4',
      title: 'Computer Systems and Programming Languages',
      description: 'Understanding how computers work, programming language concepts, and software engineering principles.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Binary and Number Systems',
          type: 'coding-interactive',
          content: 'Understanding binary representation, number system conversions, and bitwise operations.',
          exercise: {
            instructions: 'Create a function "binaryConverter" that converts a decimal number to its binary representation as a string, and also converts binary strings back to decimal. Test with binaryConverter(10, "decimal") which should return "1010".',
            initialCode: `function binaryConverter(input, fromBase) {
  if (fromBase === 'decimal') {
    // Convert decimal to binary
    if (input === 0) return '0';
    
    let result = '';
    let num = Math.abs(input);
    
    while (num > 0) {
      result = (num % 2) + result;
      num = Math.floor(num / 2);
    }
    
    return input < 0 ? '-' + result : result;
  } else if (fromBase === 'binary') {
    // Convert binary to decimal
    const isNegative = input.startsWith('-');
    const binaryStr = isNegative ? input.slice(1) : input;
    
    let result = 0;
    let power = 0;
    
    for (let i = binaryStr.length - 1; i >= 0; i--) {
      if (binaryStr[i] === '1') {
        result += Math.pow(2, power);
      }
      power++;
    }
    
    return isNegative ? -result : result;
  }
  
  return null;
}

// Test the function
console.log(binaryConverter(10, 'decimal'));`,
            expectedOutput: `"1010"`,
            hints: [
              'For decimal to binary: use repeated division by 2',
              'For binary to decimal: use powers of 2',
              'Handle the string manipulation carefully'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Memory and Data Representation',
          type: 'coding-interactive',
          content: 'Learning how data is stored in memory, pointers concepts, and memory management principles.',
          exercise: {
            instructions: 'Implement a function "memorySimulator" that simulates a simple memory allocation system. It should track allocated blocks and detect memory leaks. Test by allocating blocks and checking for leaks.',
            initialCode: `function memorySimulator() {
  const memory = {};
  let nextId = 1;
  
  return {
    allocate: function(size) {
      const id = nextId++;
      memory[id] = {
        size: size,
        allocated: true,
        timestamp: Date.now()
      };
      return id;
    },
    
    deallocate: function(id) {
      if (memory[id] && memory[id].allocated) {
        delete memory[id];
        return true;
      }
      return false;
    },
    
    getLeaks: function() {
      const leaks = [];
      for (let id in memory) {
        if (memory[id].allocated) {
          leaks.push(parseInt(id));
        }
      }
      return leaks.sort((a, b) => a - b);
    },
    
    getMemoryInfo: function() {
      let totalAllocated = 0;
      let blockCount = 0;
      
      for (let id in memory) {
        totalAllocated += memory[id].size;
        blockCount++;
      }
      
      return { totalSize: totalAllocated, blocks: blockCount };
    }
  };
}

// Test the simulator
const sim = memorySimulator();
const id1 = sim.allocate(100);
const id2 = sim.allocate(200);
const id3 = sim.allocate(50);
sim.deallocate(id2);
console.log(sim.getLeaks());`,
            expectedOutput: `[1, 3]`,
            hints: [
              'Track allocated blocks with IDs',
              'Remove blocks when deallocated',
              'Leaks are blocks that were never deallocated'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Programming Paradigms',
          type: 'coding-interactive',
          content: 'Understanding different programming paradigms: procedural, object-oriented, and functional programming.',
          exercise: {
            instructions: 'Create a functional programming solution using "mapReduceFilter" that processes an array of student objects to find the average grade of students who passed (grade >= 70). Test with students who have grades [85, 92, 65, 78, 88, 45] - passing students average should be 85.75.',
            initialCode: `function mapReduceFilter(students) {
  // Filter students who passed (grade >= 70)
  const passingStudents = students.filter(student => student.grade >= 70);
  
  // If no passing students, return 0
  if (passingStudents.length === 0) {
    return 0;
  }
  
  // Map to get just the grades, then reduce to calculate average
  const totalGrade = passingStudents
    .map(student => student.grade)
    .reduce((sum, grade) => sum + grade, 0);
  
  return totalGrade / passingStudents.length;
}

// Alternative one-liner approach
function mapReduceFilterOneLiner(students) {
  const passing = students.filter(s => s.grade >= 70);
  return passing.length ? passing.reduce((sum, s) => sum + s.grade, 0) / passing.length : 0;
}

// Test the function
const testStudents = [
  { name: "Alice", grade: 85 },
  { name: "Bob", grade: 92 },
  { name: "Charlie", grade: 65 },
  { name: "David", grade: 78 },
  { name: "Eve", grade: 88 },
  { name: "Frank", grade: 45 }
];
console.log(mapReduceFilter(testStudents));`,
            expectedOutput: `85.75`,
            hints: [
              'Filter students with grade >= 70',
              'Map to get just the grades',
              'Reduce to calculate the average'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 4 Assessment: Systems Thinking',
        instructions: 'Design a simple "CacheSystem" class that implements LRU (Least Recently Used) caching with get and put operations. Test by creating cache with capacity 2, putting (1,1), (2,2), getting 1, putting (3,3), then getting 2 should return -1.',
        initialCode: `class CacheSystem {
  constructor(capacity) {
    this.capacity = capacity;
    this.cache = new Map();
  }
  
  get(key) {
    if (this.cache.has(key)) {
      // Move to end (most recently used)
      const value = this.cache.get(key);
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }
    return -1;
  }
  
  put(key, value) {
    if (this.cache.has(key)) {
      // Update existing key
      this.cache.delete(key);
      this.cache.set(key, value);
    } else {
      // Add new key
      if (this.cache.size >= this.capacity) {
        // Remove least recently used (first item)
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }
      this.cache.set(key, value);
    }
  }
  
  // Helper method to see cache state
  getCache() {
    return Array.from(this.cache.entries());
  }
}

// Test the cache system
const cache = new CacheSystem(2);
cache.put(1, 1);
cache.put(2, 2);
console.log(cache.get(1)); // Should return 1
cache.put(3, 3); // Evicts key 2
console.log(cache.get(2)); // Should return -1`,
        expectedOutput: `-1`,
        hints: [
          'Use a Map to maintain insertion order',
          'Move accessed items to the end',
          'Remove oldest items when capacity exceeded'
        ]
      }
    },
    {
      id: 'module-5',
      title: 'Final Project and Integration',
      description: 'Combine all learned concepts in a comprehensive final project demonstrating computational thinking and problem-solving skills.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Project Planning and Design',
          type: 'coding-interactive',
          content: 'Learning software design principles, project planning, and system architecture concepts.',
          exercise: {
            instructions: 'Create a "TaskManager" class that manages a list of tasks with priorities. Implement methods to add tasks, complete tasks, and get the next highest priority task. Lower numbers = higher priority.',
            initialCode: `class TaskManager {
  constructor() {
    this.tasks = [];
    this.nextId = 1;
  }
  
  addTask(description, priority) {
    const task = {
      id: this.nextId++,
      description: description,
      priority: priority,
      completed: false,
      createdAt: new Date()
    };
    this.tasks.push(task);
    return task.id;
  }
  
  completeTask(taskId) {
    const task = this.tasks.find(t => t.id === taskId);
    if (task && !task.completed) {
      task.completed = true;
      task.completedAt = new Date();
      return true;
    }
    return false;
  }
  
  getNextTask() {
    const incompleteTasks = this.tasks.filter(task => !task.completed);
    
    if (incompleteTasks.length === 0) {
      return null;
    }
    
    // Sort by priority (lower number = higher priority)
    // Then by creation date for ties
    incompleteTasks.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
    
    return incompleteTasks[0];
  }
  
  getAllTasks() {
    return this.tasks.slice(); // Return copy
  }
  
  getCompletedTasks() {
    return this.tasks.filter(task => task.completed);
  }
}

// Test the task manager
const tm = new TaskManager();
tm.addTask("Regular task", 3);
tm.addTask("Critical bug fix", 1);
tm.addTask("Low priority", 5);
console.log(tm.getNextTask());`,
            expectedOutput: `{ id: 2, description: "Critical bug fix", priority: 1, completed: false }`,
            hints: [
              'Store tasks with unique IDs',
              'Sort by priority when getting next task',
              'Track completion status for each task'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Algorithm Optimization',
          type: 'coding-interactive',
          content: 'Understanding time and space complexity, optimization techniques, and performance analysis.',
          exercise: {
            instructions: 'Optimize this function "findDuplicates" to find all duplicate numbers in an array in O(n) time complexity using minimal extra space. Array contains integers 1 to n. Test with [4,3,2,7,8,2,3,1] which should return [2,3].',
            initialCode: `function findDuplicates(nums) {
  const duplicates = [];
  
  // Use the array indices as a hash map
  // Mark visited numbers by making them negative
  for (let i = 0; i < nums.length; i++) {
    const index = Math.abs(nums[i]) - 1; // Convert to 0-based index
    
    // If the number at this index is already negative,
    // it means we've seen this number before
    if (nums[index] < 0) {
      duplicates.push(Math.abs(nums[i]));
    } else {
      // Mark as visited by making it negative
      nums[index] = -nums[index];
    }
  }
  
  // Restore the array to original state (optional)
  for (let i = 0; i < nums.length; i++) {
    nums[i] = Math.abs(nums[i]);
  }
  
  return duplicates;
}

// Alternative approach using cyclic sort concept
function findDuplicatesCyclic(nums) {
  const duplicates = [];
  
  for (let i = 0; i < nums.length; i++) {
    while (nums[i] !== i + 1 && nums[nums[i] - 1] !== nums[i]) {
      // Swap nums[i] with nums[nums[i] - 1]
      const temp = nums[nums[i] - 1];
      nums[nums[i] - 1] = nums[i];
      nums[i] = temp;
    }
  }
  
  // Find duplicates
  for (let i = 0; i < nums.length; i++) {
    if (nums[i] !== i + 1) {
      duplicates.push(nums[i]);
    }
  }
  
  return duplicates;
}

// Test the function
console.log(findDuplicates([4,3,2,7,8,2,3,1]));`,
            expectedOutput: `[2, 3]`,
            hints: [
              'Use the array indices as a hash mechanism',
              'Mark visited elements by making them negative',
              'Duplicates will try to mark already marked positions'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Testing and Debugging',
          type: 'coding-interactive',
          content: 'Learning systematic approaches to testing, debugging techniques, and code quality assurance.',
          exercise: {
            instructions: 'Create a comprehensive test function "testSortingFunction" that validates a sorting function works correctly with various test cases including edge cases. Test with a working sort function should return true.',
            initialCode: `function testSortingFunction(sortFunc) {
  const testCases = [
    // Test case: [input, expected_output, description]
    [[], [], "Empty array"],
    [[1], [1], "Single element"],
    [[1, 2], [1, 2], "Already sorted"],
    [[2, 1], [1, 2], "Reverse sorted"],
    [[3, 1, 4, 1, 5], [1, 1, 3, 4, 5], "Array with duplicates"],
    [[5, 4, 3, 2, 1], [1, 2, 3, 4, 5], "Completely reverse"],
    [[1, 1, 1, 1], [1, 1, 1, 1], "All same elements"],
    [[-3, -1, -4, -1, -5], [-5, -4, -3, -1, -1], "Negative numbers"],
    [[0, -1, 1, 0], [-1, 0, 0, 1], "Mixed with zeros"],
    [[100, 1, 50, 25, 75], [1, 25, 50, 75, 100], "Random order"]
  ];
  
  for (let i = 0; i < testCases.length; i++) {
    const [input, expected, description] = testCases[i];
    const inputCopy = [...input]; // Make a copy to avoid modifying original
    
    try {
      const result = sortFunc(inputCopy);
      
      // Check if result matches expected
      if (!arraysEqual(result, expected)) {
        console.log("Test failed: " + description);
        console.log("Input: [" + input.join(", ") + "]");
        console.log("Expected: [" + expected.join(", ") + "]");
        console.log("Got: [" + result.join(", ") + "]");
        return false;
      }
    } catch (error) {
      console.log("Test failed with error: " + description + " - " + error.message);
      return false;
    }
  }
  
  // Performance test with larger array
  const largeArray = Array.from({length: 1000}, () => Math.floor(Math.random() * 1000));
  const largeCopy = [...largeArray];
  
  try {
    const startTime = Date.now();
    const sortedLarge = sortFunc(largeCopy);
    const endTime = Date.now();
    
    // Verify it's actually sorted
    for (let i = 1; i < sortedLarge.length; i++) {
      if (sortedLarge[i] < sortedLarge[i-1]) {
        console.log("Large array sort failed - not properly sorted");
        return false;
      }
    }
    
    console.log("Performance test passed in " + (endTime - startTime) + "ms");
  } catch (error) {
    console.log("Performance test failed: " + error.message);
    return false;
  }
  
  return true;
}

// Helper function to compare arrays
function arraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}

// Test with a sample sorting function (bubble sort)
function bubbleSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
  return arr;
}

console.log(testSortingFunction(bubbleSort));`,
            expectedOutput: `true`,
            hints: [
              'Test empty arrays, single elements, already sorted arrays',
              'Test reverse sorted arrays and arrays with duplicates',
              'Compare results with expected sorted arrays'
            ]
          }
        }
      ],
      finalProject: {
        type: 'coding-interactive',
        title: 'Final Project: Student Management System',
        instructions: 'Build a comprehensive StudentManagementSystem class that manages student records, calculates GPAs, handles course enrollments, and generates academic reports. Demonstrate all CS concepts learned. Test by adding students John and Jane with different courses.',
        initialCode: `class StudentManagementSystem {
  constructor() {
    this.students = new Map(); // Use Map for O(1) lookups
    this.courses = new Map();
    this.enrollments = new Map(); // studentId -> [enrollments]
  }
  
  addStudent(id, name, email = '') {
    if (this.students.has(id)) {
      throw new Error("Student with ID " + id + " already exists");
    }
    
    const student = {
      id: id,
      name: name,
      email: email,
      createdAt: new Date(),
      active: true
    };
    
    this.students.set(id, student);
    this.enrollments.set(id, []);
    return student;
  }
  
  addCourse(courseId, courseName, credits) {
    if (this.courses.has(courseId)) {
      throw new Error("Course with ID " + courseId + " already exists");
    }
    
    const course = {
      id: courseId,
      name: courseName,
      credits: credits,
      active: true
    };
    
    this.courses.set(courseId, course);
    return course;
  }
  
  enrollStudent(studentId, courseId, grade = null) {
    if (!this.students.has(studentId)) {
      throw new Error("Student with ID " + studentId + " not found");
    }
    
    if (!this.courses.has(courseId)) {
      throw new Error("Course with ID " + courseId + " not found");
    }
    
    const enrollment = {
      studentId: studentId,
      courseId: courseId,
      grade: grade,
      enrolledAt: new Date(),
      completed: grade !== null
    };
    
    const studentEnrollments = this.enrollments.get(studentId);
    
    // Check if already enrolled
    const alreadyEnrolled = studentEnrollments.find(e => e.courseId === courseId);
    if (alreadyEnrolled) {
      throw new Error("Student already enrolled in course " + courseId);
    }
    
    studentEnrollments.push(enrollment);
    return enrollment;
  }
  
  updateGrade(studentId, courseId, grade) {
    const studentEnrollments = this.enrollments.get(studentId);
    if (!studentEnrollments) {
      throw new Error("Student with ID " + studentId + " not found");
    }
    
    const enrollment = studentEnrollments.find(e => e.courseId === courseId);
    if (!enrollment) {
      throw new Error("Enrollment not found for student " + studentId + " in course " + courseId);
    }
    
    enrollment.grade = grade;
    enrollment.completed = true;
    enrollment.gradedAt = new Date();
    return enrollment;
  }
  
  calculateGPA(studentId) {
    const studentEnrollments = this.enrollments.get(studentId);
    if (!studentEnrollments) {
      throw new Error("Student with ID " + studentId + " not found");
    }
    
    const completedEnrollments = studentEnrollments.filter(e => e.completed && e.grade !== null);
    
    if (completedEnrollments.length === 0) {
      return 0.0;
    }
    
    let totalGradePoints = 0;
    let totalCredits = 0;
    
    completedEnrollments.forEach(enrollment => {
      const course = this.courses.get(enrollment.courseId);
      const gradePoints = this.gradeToPoints(enrollment.grade);
      
      totalGradePoints += gradePoints * course.credits;
      totalCredits += course.credits;
    });
    
    return totalCredits > 0 ? +(totalGradePoints / totalCredits).toFixed(2) : 0.0;
  }
  
  gradeToPoints(grade) {
    const gradeMap = {
      'A+': 4.0, 'A': 4.0, 'A-': 3.7,
      'B+': 3.3, 'B': 3.0, 'B-': 2.7,
      'C+': 2.3, 'C': 2.0, 'C-': 1.7,
      'D+': 1.3, 'D': 1.0, 'F': 0.0
    };
    
    return gradeMap[grade] || 0.0;
  }
  
  getStudentTranscript(studentId) {
    const student = this.students.get(studentId);
    if (!student) {
      throw new Error("Student with ID " + studentId + " not found");
    }
    
    const enrollments = this.enrollments.get(studentId);
    const transcript = {
      student: student,
      courses: [],
      gpa: this.calculateGPA(studentId),
      totalCredits: 0,
      completedCredits: 0
    };
    
    enrollments.forEach(enrollment => {
      const course = this.courses.get(enrollment.courseId);
      transcript.courses.push({
        courseId: course.id,
        courseName: course.name,
        credits: course.credits,
        grade: enrollment.grade,
        completed: enrollment.completed
      });
      
      transcript.totalCredits += course.credits;
      if (enrollment.completed) {
        transcript.completedCredits += course.credits;
      }
    });
    
    return transcript;
  }
  
  generateReport() {
    const allStudents = Array.from(this.students.values());
    const totalStudents = allStudents.length;
    
    if (totalStudents === 0) {
      return {
        totalStudents: 0,
        averageGPA: 0,
        topStudent: null,
        summary: "No students in system"
      };
    }
    
    // Calculate GPAs for all students
    const studentGPAs = allStudents.map(student => ({
      student: student,
      gpa: this.calculateGPA(student.id)
    })).filter(item => item.gpa > 0); // Only include students with grades
    
    // Calculate average GPA
    const totalGPA = studentGPAs.reduce((sum, item) => sum + item.gpa, 0);
    const averageGPA = studentGPAs.length > 0 ? +(totalGPA / studentGPAs.length).toFixed(2) : 0;
    
    // Find top student
    let topStudent = null;
    if (studentGPAs.length > 0) {
      studentGPAs.sort((a, b) => b.gpa - a.gpa);
      topStudent = studentGPAs[0].student.name;
    }
    
    // Additional statistics
    const totalEnrollments = Array.from(this.enrollments.values())
      .reduce((sum, enrollments) => sum + enrollments.length, 0);
    
    const completedEnrollments = Array.from(this.enrollments.values())
      .reduce((sum, enrollments) => sum + enrollments.filter(e => e.completed).length, 0);
    
    return {
      totalStudents: totalStudents,
      averageGPA: averageGPA,
      topStudent: topStudent,
      totalEnrollments: totalEnrollments,
      completedEnrollments: completedEnrollments,
      completionRate: totalEnrollments > 0 ? +((completedEnrollments / totalEnrollments) * 100).toFixed(1) : 0,
      studentsWithGrades: studentGPAs.length,
      summary: totalStudents + " students, " + studentGPAs.length + " with grades, avg GPA " + averageGPA
    };
  }
  
  // Search functionality
  searchStudents(query) {
    const results = [];
    const lowerQuery = query.toLowerCase();
    
    this.students.forEach(student => {
      if (student.name.toLowerCase().includes(lowerQuery) || 
          student.id.toString().includes(query) ||
          student.email.toLowerCase().includes(lowerQuery)) {
        results.push(student);
      }
    });
    
    return results;
  }
  
  // Bulk operations
  bulkUpdateGrades(updates) {
    const results = [];
    
    updates.forEach(update => {
      try {
        const enrollment = this.updateGrade(update.studentId, update.courseId, update.grade);
        results.push({ success: true, enrollment: enrollment });
      } catch (error) {
        results.push({ success: false, error: error.message, update: update });
      }
    });
    
    return results;
  }
}

// Test the system
const sms = new StudentManagementSystem();

// Add courses
sms.addCourse('CS101', 'Intro to Computer Science', 3);
sms.addCourse('MATH201', 'Calculus I', 4);
sms.addCourse('ENG101', 'English Composition', 3);

// Add students
sms.addStudent(1, 'John Doe', 'john@example.com');
sms.addStudent(2, 'Jane Smith', 'jane@example.com');

// Enroll students and add grades
sms.enrollStudent(1, 'CS101');
sms.updateGrade(1, 'CS101', 'A');
sms.enrollStudent(1, 'MATH201');
sms.updateGrade(1, 'MATH201', 'B+');

sms.enrollStudent(2, 'CS101');
sms.updateGrade(2, 'CS101', 'B');
sms.enrollStudent(2, 'ENG101');
sms.updateGrade(2, 'ENG101', 'A-');

// Generate report
console.log(sms.generateReport());`,
        expectedOutput: `{ totalStudents: 2, averageGPA: 3.4, topStudent: "John Doe" }`,
        hints: [
          'Use appropriate data structures for efficient lookups',
          'Implement GPA calculation algorithms correctly',
          'Consider edge cases and error handling',
          'Optimize for performance with large datasets'
        ]
      }
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Algorithm Analysis Project',
      description: 'Compare different sorting algorithms and analyze their time complexities',
      type: 'project',
      dueDate: 'Week 6',
      points: 100
    },
    {
      id: 'assignment-2',
      title: 'Recursive Problem Solving',
      description: 'Solve complex problems using recursive approaches',
      type: 'coding',
      dueDate: 'Week 9',
      points: 75
    },
    {
      id: 'assignment-3',
      title: 'Data Structure Implementation',
      description: 'Implement and test custom data structures',
      type: 'coding',
      dueDate: 'Week 12',
      points: 100
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      points: 150,
      topics: ['Computational Thinking', 'Data Structures', 'Basic Algorithms']
    },
    {
      id: 'final',
      title: 'Final Project Presentation',
      type: 'presentation',
      date: 'Week 15',
      points: 200,
      topics: ['Complete System Design', 'Algorithm Implementation', 'Performance Analysis']
    }
  ]
};